/**
 * EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-SHIFT-TIME-UX-V1
 *
 * Pure helpers for shift time/date entry. No React, no fetch.
 *
 * Time normalisation rules (string → "HH:MM" or null when invalid):
 *   "8"     → "08:00"
 *   "08"    → "08:00"
 *   "8:30"  → "08:30"
 *   "8:3"   → "08:30"   (treats single trailing minute digit as tens)
 *   "830"   → "08:30"
 *   "0830"  → "08:30"
 *   "9.5"   → "09:30"   (decimal hours)
 *   "12:00" → "12:00"
 *   "25:00" → null (invalid)
 *   "8:60"  → null (invalid)
 *   "abc"   → null
 *   ""      → ""        (caller decides whether empty is allowed)
 */

const _twoDigits = (n) => (n < 10 ? `0${n}` : `${n}`);

export function normaliseTime(raw) {
  if (raw === null || raw === undefined) return "";
  const s = String(raw).trim();
  if (s === "") return "";

  // 1. "9.5" / "8.25" → decimal hours.
  if (/^\d+(\.\d+)?$/.test(s) && s.includes(".")) {
    const v = parseFloat(s);
    if (Number.isFinite(v) && v >= 0 && v < 24) {
      const h = Math.floor(v);
      const m = Math.round((v - h) * 60);
      if (m === 60) {
        return h + 1 < 24 ? `${_twoDigits(h + 1)}:00` : null;
      }
      return `${_twoDigits(h)}:${_twoDigits(m)}`;
    }
    return null;
  }

  // 2. "HH:MM" / "H:MM" / "HH:M" / "H:M".
  if (s.includes(":")) {
    const parts = s.split(":");
    if (parts.length !== 2) return null;
    const [hraw, mraw] = parts;
    if (!/^\d{1,2}$/.test(hraw) || !/^\d{1,2}$/.test(mraw)) return null;
    let h = parseInt(hraw, 10);
    let m = parseInt(mraw, 10);
    // Single-digit minute → treat as tens (so "8:3" → 08:30, which the
    // user almost certainly meant). This matches the spec.
    if (mraw.length === 1) m = m * 10;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return `${_twoDigits(h)}:${_twoDigits(m)}`;
  }

  // 3. Bare digits.
  if (!/^\d+$/.test(s)) return null;
  if (s.length === 1 || s.length === 2) {
    // "8" or "08" → 08:00
    const h = parseInt(s, 10);
    if (h < 0 || h > 23) return null;
    return `${_twoDigits(h)}:00`;
  }
  if (s.length === 3) {
    // "830" → 08:30
    const h = parseInt(s.slice(0, 1), 10);
    const m = parseInt(s.slice(1), 10);
    if (h > 23 || m > 59) return null;
    return `${_twoDigits(h)}:${_twoDigits(m)}`;
  }
  if (s.length === 4) {
    // "0830" → 08:30
    const h = parseInt(s.slice(0, 2), 10);
    const m = parseInt(s.slice(2), 10);
    if (h > 23 || m > 59) return null;
    return `${_twoDigits(h)}:${_twoDigits(m)}`;
  }
  return null;
}

/** Returns numeric hours between two "HH:MM" strings on the same day,
 *  or null if either is empty/invalid or the result is non-positive. */
export function diffHours(startHHMM, endHHMM) {
  if (!startHHMM || !endHHMM) return null;
  const [sh, sm] = startHHMM.split(":").map((x) => parseInt(x, 10));
  const [eh, em] = endHHMM.split(":").map((x) => parseInt(x, 10));
  if (
    !Number.isFinite(sh) ||
    !Number.isFinite(sm) ||
    !Number.isFinite(eh) ||
    !Number.isFinite(em)
  )
    return null;
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  if (endMin <= startMin) return null;
  return (endMin - startMin) / 60;
}

/** Returns "HH:MM" from start + decimal-hour duration, or null. */
export function addHours(startHHMM, durationHours) {
  if (!startHHMM) return null;
  const d = parseFloat(durationHours);
  if (!Number.isFinite(d) || d <= 0) return null;
  const [sh, sm] = startHHMM.split(":").map((x) => parseInt(x, 10));
  if (!Number.isFinite(sh) || !Number.isFinite(sm)) return null;
  const totalMin = sh * 60 + sm + Math.round(d * 60);
  if (totalMin <= 0 || totalMin >= 24 * 60) return null; // overflow → caller decides
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  return `${_twoDigits(hh)}:${_twoDigits(mm)}`;
}

/** Build an inclusive list of "YYYY-MM-DD" strings from start to end. */
export function expandDateRange(startISO, endISO, { max = 31 } = {}) {
  if (!startISO || !endISO) return [];
  const start = new Date(`${startISO}T00:00:00Z`);
  const end = new Date(`${endISO}T00:00:00Z`);
  if (
    !Number.isFinite(start.getTime()) ||
    !Number.isFinite(end.getTime()) ||
    end.getTime() < start.getTime()
  ) {
    return [];
  }
  const out = [];
  for (let i = 0; i < max + 1; i += 1) {
    const d = new Date(start.getTime() + i * 86400000);
    const iso = d.toISOString().slice(0, 10);
    out.push(iso);
    if (iso === endISO) break;
  }
  if (out[out.length - 1] !== endISO) return out; // range exceeded `max`
  return out;
}

/** Format duration for display: 9.5 → "9h30", 8 → "8h00". */
export function formatDurationHM(hours) {
  if (hours === null || hours === undefined || !Number.isFinite(hours))
    return "";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h${_twoDigits(m)}`;
}
